import * as React from 'react';

// --- Lib ---
import { parseShortcutKeys } from '../../../../app/rte/lib/tiptap-utils';

// --- Hooks ---
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import type { UseColorTextConfig } from './use-color-text';
import { COLOR_TEXT_SHORTCUT_KEY, useColorText } from './use-color-text';

// --- UI Primitives ---
import { Badge } from '@cotera/client/v0/components/ui/badge';

import { Button } from '@cotera/client/v0/components/ui/button';
import { Tooltip } from '@cotera/client/app/components';

// --- Styles ---

export interface ColorTextButtonProps
  extends Omit<React.ComponentProps<'button'>, 'type'>,
    UseColorTextConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;

  tooltip?: string;
}

export function ColorTextShortcutBadge({
  shortcutKeys = COLOR_TEXT_SHORTCUT_KEY,
}: {
  shortcutKeys?: string;
}) {
  return <Badge variant="outline">{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for applying text colors in a Tiptap editor.
 *
 * For custom button implementations, use the `useColorText` hook instead.
 */
export const ColorTextButton = React.forwardRef<
  HTMLButtonElement,
  ColorTextButtonProps
>(
  (
    {
      editor: providedEditor,
      textColor,
      text,
      hideWhenUnavailable = false,
      onApplied,
      showShortcut = false,
      onClick,
      children,
      style,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const {
      isVisible,
      canColorText,
      isActive,
      handleColorText,
      label,
      shortcutKeys,
      Icon,
    } = useColorText({
      editor,
      textColor,
      label: text || `Color text to ${textColor}`,
      hideWhenUnavailable,
      onApplied,
    });

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        handleColorText();
      },
      [handleColorText, onClick]
    );

    const buttonStyle = React.useMemo(
      () =>
        ({
          ...style,
          '--color-text-button-color': textColor,
        } as React.CSSProperties),
      [textColor, style]
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Tooltip side="auto" tooltipContent={label}>
        <Button
          variant="ghost"
          data-active-state={isActive ? 'on' : 'off'}
          role="button"
          tabIndex={-1}
          disabled={!canColorText}
          data-disabled={!canColorText}
          aria-label={label}
          aria-pressed={isActive}
          onClick={handleClick}
          style={buttonStyle}
          {...buttonProps}
          ref={ref}
        >
          {children ?? (
            <>
              <span style={{ color: textColor }}>
                <Icon
                  className="tiptap-button-icon"
                  style={{ color: textColor, flexGrow: 1 }}
                />
              </span>
              {text && <span className="tiptap-button-text">{text}</span>}
              {showShortcut && (
                <ColorTextShortcutBadge shortcutKeys={shortcutKeys} />
              )}
            </>
          )}
        </Button>
      </Tooltip>
    );
  }
);

ColorTextButton.displayName = 'ColorTextButton';
